import {
  proposeTaper,
  quitDateOf,
  upcomingSteps,
  validatePrice,
  validateTaper,
  type FundLike,
  type LocalDate,
  type PlanDraft,
  type Price,
  type TaperDraft,
} from '@tahi/core';
import { useId, useState, type ChangeEvent } from 'react';
import { Button, ScreenHeader } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { TextField } from '../../../ui/TextField';
import { copy, errorText, QUIT_BACK } from '../copy';
import { minorToPesos, pesosToMinor, shortDate } from '../format';
import { CARD, INPUT, LABEL, SCREEN } from '../styles';

type PriceMode = Price['kind'];
type TaperText = Record<keyof TaperDraft, string>;

interface Props {
  mode: 'new' | 'edit';
  initial: PlanDraft | null;
  today: LocalDate;
  defaultCountry: string;
  /** Budget funds (v0.2 `listFunds`; only id and name are shown), or null when Budget is off (no picker). */
  funds: Pick<FundLike, 'id' | 'name'>[] | null;
  /** Resolves to an error message, or null when saved. */
  onSave(draft: PlanDraft): Promise<string | null>;
}

const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : Number.NaN);
const taperText = (t: TaperDraft): TaperText => ({
  startLimit: String(t.startLimit),
  step: String(t.step),
  everyDays: String(t.everyDays),
  floor: String(t.floor),
});

export function PlanWizard({ mode, initial, today, defaultCountry, funds, onSave }: Props) {
  const fundSelectId = useId();
  const p = initial?.price;
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState(initial ? String(initial.baselinePerDay) : '');
  const [priceMode, setPriceMode] = useState<PriceMode>(p?.kind ?? 'pack');
  const [packPrice, setPackPrice] = useState(
    p?.kind === 'pack' ? minorToPesos(p.packPriceMinor) : '',
  );
  const [perPack, setPerPack] = useState(p?.kind === 'pack' ? String(p.perPack) : '20');
  const [stickPrice, setStickPrice] = useState(
    p?.kind === 'stick' ? minorToPesos(p.stickPriceMinor) : '',
  );
  const [taper, setTaper] = useState<TaperText>(
    initial ? taperText(initial.taper) : { startLimit: '', step: '', everyDays: '', floor: '' },
  );
  const [touched, setTouched] = useState(mode === 'edit');
  const [fundId, setFundId] = useState<string | null>(initial?.fundId ?? null);

  function readPrice(): Price | null {
    if (priceMode === 'none') return { kind: 'none' };
    if (priceMode === 'stick') {
      const m = pesosToMinor(stickPrice);
      return m === null ? null : { kind: 'stick', stickPriceMinor: m };
    }
    const m = pesosToMinor(packPrice);
    const n = int(perPack);
    return m === null || Number.isNaN(n) ? null : { kind: 'pack', packPriceMinor: m, perPack: n };
  }

  const readTaper = (): TaperDraft => ({
    startLimit: int(taper.startLimit),
    step: int(taper.step),
    everyDays: int(taper.everyDays),
    floor: int(taper.floor),
  });

  function next(): void {
    setError(null);
    if (step === 0) {
      const b = int(baseline);
      if (Number.isNaN(b) || b < 1 || b > 100) {
        setError(errorText('bad_baseline'));
        return;
      }
      if (!touched) setTaper(taperText(proposeTaper(b)));
      setStep(1);
      return;
    }
    const price = readPrice();
    if (!price || validatePrice(price)) {
      setError(errorText('bad_price'));
      return;
    }
    setStep(2);
  }

  async function save(): Promise<void> {
    setError(null);
    const t = readTaper();
    const taperError = validateTaper(t);
    if (taperError) {
      setError(errorText(taperError));
      return;
    }
    const price = readPrice();
    if (!price) {
      setError(errorText('bad_price'));
      return;
    }
    const message = await onSave({
      baselinePerDay: int(baseline),
      taper: t,
      price,
      fundId,
      quitlineCountry: initial?.quitlineCountry ?? defaultCountry,
    });
    if (message) setError(message);
  }

  const onTaper = (k: keyof TaperDraft) => (e: ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    setTaper({ ...taper, [k]: e.target.value });
  };
  const draft = readTaper();
  const seg = [{ from: today, ...draft }];
  const previewOk = validateTaper(draft) === null;
  const quitDate = previewOk ? quitDateOf(seg) : null;

  return (
    <main className={SCREEN}>
      <ScreenHeader title={mode === 'new' ? copy.wizardNew : copy.wizardEdit} back={QUIT_BACK} />
      <p className={LABEL}>{copy.stepOf(step + 1, 3)}</p>

      {step === 0 ? (
        <section className={`${CARD} flex flex-col gap-3`}>
          <h2 className="text-lg text-fg">{copy.stepBaseline}</h2>
          <TextField
            label={copy.baseline}
            type="number"
            inputMode="numeric"
            value={baseline}
            onChange={(e) => setBaseline(e.target.value)}
          />
        </section>
      ) : null}

      {step === 1 ? (
        <section className={`${CARD} flex flex-col gap-3`}>
          <h2 className="text-lg text-fg">{copy.stepPrice}</h2>
          <Segmented<PriceMode>
            label={copy.priceMode}
            value={priceMode}
            onChange={setPriceMode}
            options={[
              { value: 'pack', label: copy.perPackMode },
              { value: 'stick', label: copy.perStickMode },
              { value: 'none', label: copy.noPriceMode },
            ]}
          />
          {priceMode === 'pack' ? (
            <>
              <TextField
                label={copy.packPrice}
                inputMode="decimal"
                value={packPrice}
                onChange={(e) => setPackPrice(e.target.value)}
              />
              <TextField
                label={copy.perPack}
                type="number"
                inputMode="numeric"
                value={perPack}
                onChange={(e) => setPerPack(e.target.value)}
              />
            </>
          ) : null}
          {priceMode === 'stick' ? (
            <TextField
              label={copy.stickPrice}
              inputMode="decimal"
              value={stickPrice}
              onChange={(e) => setStickPrice(e.target.value)}
            />
          ) : null}
        </section>
      ) : null}

      {step === 2 ? (
        <section className={`${CARD} flex flex-col gap-3`}>
          <h2 className="text-lg text-fg">{copy.stepSchedule}</h2>
          <p className="text-sm text-muted">{copy.scheduleHint}</p>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={copy.startLimit}
              type="number"
              inputMode="numeric"
              value={taper.startLimit}
              onChange={onTaper('startLimit')}
            />
            <TextField
              label={copy.step}
              type="number"
              inputMode="numeric"
              value={taper.step}
              onChange={onTaper('step')}
            />
            <TextField
              label={copy.everyDays}
              type="number"
              inputMode="numeric"
              value={taper.everyDays}
              onChange={onTaper('everyDays')}
            />
            <TextField
              label={copy.floor}
              type="number"
              inputMode="numeric"
              value={taper.floor}
              onChange={onTaper('floor')}
            />
          </div>
          {previewOk ? (
            <div
              role="group"
              aria-label={copy.preview}
              className="rounded-2xl bg-surface p-3 text-sm"
            >
              <p className="text-fg">
                {quitDate ? copy.quitDate(shortDate(quitDate)) : copy.cuttingTo(draft.floor)}
              </p>
              <ul className="mt-1 text-muted">
                {upcomingSteps(seg, today, 3).map((s) => (
                  <li key={s.date}>{copy.nextStep(shortDate(s.date), s.limit)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {funds ? (
            <div className="flex flex-col gap-1">
              <label htmlFor={fundSelectId} className="text-sm font-medium text-muted">
                {copy.fund}
              </label>
              <select
                id={fundSelectId}
                className={INPUT}
                value={fundId ?? ''}
                onChange={(e) => setFundId(e.target.value || null)}
              >
                <option value="">{copy.fundDefault}</option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </section>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        {step > 0 ? (
          <Button
            variant="secondary"
            onClick={() => {
              setError(null);
              setStep(step - 1);
            }}
          >
            {copy.back}
          </Button>
        ) : null}
        {step < 2 ? (
          <Button variant="primary" block onClick={next}>
            {copy.next}
          </Button>
        ) : (
          <Button variant="primary" block onClick={() => void save()}>
            {copy.savePlan}
          </Button>
        )}
      </div>
    </main>
  );
}
